import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  InputAdornment,
  Autocomplete,
  Paper,
} from '@mui/material';
import { ListPlus, User, X, Music, UserCheck, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AddToPlaylistDialog({ 
  show, 
  video, 
  close, 
  addToPlayList, 
  targetPlaylistName,
  existingParticipants = [],
  isIntegratedScreen = false,
}) {
  const { t } = useTranslation();
  const [assignedTo, setAssignedTo] = useState('');

  // Limpiar el campo cada vez que se abre con un video nuevo
  useEffect(() => {
    if (show) {
      setAssignedTo('');
    }
  }, [show, video]);

  const handleConfirm = (nameToUse) => {
    if (!video) return;
    const finalName = typeof nameToUse === 'string' ? nameToUse : assignedTo;
    addToPlayList(video, finalName.trim());
    close();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
    }
  };

  if (!video) return null;

  const thumbnail =
    video.snippet?.thumbnails?.medium?.url ||
    video.snippet?.thumbnails?.high?.url ||
    video.snippet?.thumbnails?.default?.url;

  const title = video.snippet?.title || 'Canción seleccionada';
  const channel = video.snippet?.channelTitle || 'YouTube';

  return (
    <Dialog
      open={show}
      onClose={close}
      maxWidth={isIntegratedScreen ? false : 'sm'}
      fullWidth
      sx={isIntegratedScreen ? {
        '& .MuiDialog-container': {
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: { xs: '100%', md: '30%' },
          minWidth: { md: '320px' },
          maxWidth: { md: '30vw' },
          p: { xs: 1, sm: 1.5 },
          boxSizing: 'border-box',
          zIndex: 1300,
        },
        '& .MuiBackdrop-root': {
          width: { xs: '100%', md: '30%' },
          minWidth: { md: '320px' },
          maxWidth: { md: '30vw' },
          background: 'rgba(6, 1, 14, 0.85)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        },
        '& .MuiDialog-paper': {
          width: '100% !important',
          maxWidth: '100% !important',
          m: '0 !important',
        }
      } : {}}
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(6, 1, 14, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: isIntegratedScreen ? '100%' : '94vw',
          maxWidth: isIntegratedScreen ? '100%' : '520px',
          borderRadius: isIntegratedScreen ? '16px' : '22px',
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          WebkitBackdropFilter: 'none !important',
          border: '1px solid rgba(255, 45, 155, 0.35)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 35px rgba(255, 45, 155, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          m: isIntegratedScreen ? 0 : 'auto',
        },
      }}
    >
      {/* Header del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: isIntegratedScreen ? 2 : { xs: 2.5, sm: 3 },
          py: isIntegratedScreen ? 1.5 : 2,
          mb: isIntegratedScreen ? 1 : 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: '#150a26',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.2) 0%, rgba(180, 77, 255, 0.2) 100%)',
              border: '1px solid rgba(255, 45, 155, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ff2d9b',
              boxShadow: '0 0 15px rgba(255, 45, 155, 0.3)',
            }}
          >
            <ListPlus size={20} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.05rem', sm: '1.18rem' },
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
              }}
            >
              {t('search.selectPlaylistToAdd')}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.55)', mt: 0.3 }}>
              {targetPlaylistName 
                ? `"${targetPlaylistName}"` 
                : ''}
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="close"
          onClick={close}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.65)',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
            p: '7px',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: '#ffffff',
              background: 'rgba(255, 45, 155, 0.2)',
              borderColor: 'rgba(255, 45, 155, 0.5)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Cuerpo del Modal */}
      <DialogContent
        sx={{
          p: isIntegratedScreen ? 2 : { xs: 2.5, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: isIntegratedScreen ? 2 : 2.5,
        }}
      >
        {/* Tarjeta de preview del video seleccionado */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            borderRadius: '14px',
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.05)',
            transition: 'all 0.25s ease',
            '&:hover': {
              borderColor: 'rgba(255, 45, 155, 0.3)',
              background: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          {thumbnail && (
            <Box
              component="img"
              src={thumbnail}
              alt={title}
              sx={{
                width: 105,
                height: 68,
                borderRadius: '9px',
                objectFit: 'cover',
                flexShrink: 0,
                border: '1px solid rgba(255, 255, 255, 0.12)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
              }}
            />
          )}

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.92rem',
                fontWeight: 600,
                color: '#ffffff',
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.6 }}>
              <Music size={13} color="#00f2fe" />
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: 'rgba(255, 255, 255, 0.6)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {channel}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Input Asignar a con Predictivo */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' }}>
              {t('modals.assignSingerQuestion')}
            </Typography>
            {existingParticipants.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Sparkles size={12} color="#00ff88" />
                <Typography sx={{ fontSize: '0.72rem', color: '#00ff88', fontWeight: 500 }}>
                  {t('modals.detectedParticipants', { count: existingParticipants.length })}
                </Typography>
              </Box>
            )}
          </Box>

          <Autocomplete
            freeSolo
            autoHighlight
            options={existingParticipants}
            value={assignedTo}
            onChange={(event, newValue) => {
              if (typeof newValue === 'string') {
                setAssignedTo(newValue);
              } else if (newValue) {
                setAssignedTo(newValue.toString());
              } else {
                setAssignedTo('');
              }
            }}
            onInputChange={(event, newInputValue) => {
              setAssignedTo(newInputValue);
            }}
            slotProps={{
              paper: {
                sx: {
                  background: 'rgba(18, 8, 32, 0.98)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255, 45, 155, 0.3)',
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.7), 0 0 15px rgba(255, 45, 155, 0.2)',
                  color: '#ffffff',
                  my: 0.5,
                  '& .MuiAutocomplete-noOptions': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: '0.82rem',
                  },
                }
              }
            }}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option}
                sx={{
                  py: 1,
                  px: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  fontSize: '0.86rem',
                  color: '#ffffff',
                  transition: 'all 0.15s ease',
                  '&:hover, &.Mui-focused': {
                    background: 'rgba(255, 45, 155, 0.15) !important',
                    color: '#ff2d9b !important',
                  }
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '8px',
                    background: 'rgba(255, 45, 155, 0.15)',
                    border: '1px solid rgba(255, 45, 155, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#ff2d9b',
                    flexShrink: 0
                  }}
                >
                  <UserCheck size={14} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: 'inherit' }}>
                    {option}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.4)', textTransform: 'uppercase' }}>
                  {t('modals.participant')}
                </Typography>
              </Box>
            )}
            renderInput={(params) => {
              const { InputProps, ...restParams } = params;
              return (
                <TextField
                  {...restParams}
                  fullWidth
                  autoFocus
                  size="medium"
                  placeholder={t('modals.assignSingerPlaceholder')}
                  onKeyDown={handleKeyDown}
                  inputProps={{
                    ...params.inputProps,
                    maxLength: 25,
                  }}
                  InputProps={{
                    ...InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <User size={18} color="#ff2d9b" />
                        </InputAdornment>
                        {InputProps?.startAdornment}
                      </>
                    ),
                  }}
                  helperText={
                    assignedTo.trim() && !existingParticipants.some(p => p.toLowerCase() === assignedTo.trim().toLowerCase())
                      ? `${t('modals.participant')}: "${assignedTo.trim()}"`
                      : ''
                  }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    color: '#ffffff',
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.12)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 45, 155, 0.4)' },
                    '&.Mui-focused fieldset': { borderColor: '#ff2d9b', borderWidth: 2 },
                  },
                  '& .MuiFormHelperText-root': {
                    color: assignedTo.trim() && !existingParticipants.some(p => p.toLowerCase() === assignedTo.trim().toLowerCase())
                      ? '#00ff88'
                      : 'rgba(255, 255, 255, 0.5)',
                    fontSize: '0.75rem',
                    mx: 0.5,
                    mt: 0.8,
                    fontWeight: assignedTo.trim() && !existingParticipants.some(p => p.toLowerCase() === assignedTo.trim().toLowerCase()) ? 600 : 400
                  },
                }}
              />
            );
          }}
          />
        </Box>
      </DialogContent>

      {/* Footer del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 1.5,
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <Button
          variant="outlined"
          onClick={close}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            color: 'rgba(255, 255, 255, 0.75)',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            fontSize: '0.88rem',
            px: 2.5,
            py: 0.8,
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.35)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#ffffff',
            },
          }}
        >
          {t('common.cancel')}
        </Button>

        <Button
          variant="contained"
          id="addToPlaylistButton"
          startIcon={<ListPlus size={18} />}
          onClick={handleConfirm}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
            color: '#ffffff',
            fontWeight: 600,
            fontSize: '0.88rem',
            px: 3,
            py: 0.8,
            boxShadow: '0 4px 18px rgba(255, 45, 155, 0.45)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #ff40a7 0%, #be61ff 100%)',
              boxShadow: '0 6px 24px rgba(255, 45, 155, 0.65)',
              transform: 'scale(1.02)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
          }}
        >
          {t('search.selectPlaylistToAdd')}
        </Button>
      </Box>
    </Dialog>
  );
}